import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useApp } from "../contexts/AppContext";
import { useAuth } from "../contexts/AuthContext";
import { formatApiError } from "../lib/api";
import { LogIn, Mail, Lock, User, Phone, Shield } from "lucide-react";

const AuthPage = ({ mode = "login", admin = false }) => {
    const { t } = useApp();
    const { login, adminLogin, register } = useAuth();
    const nav = useNavigate();
    const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
    const [loading, setLoading] = useState(false);
    const isRegister = mode === "register";

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            if (admin) {
                await adminLogin(form.email, form.password);
                nav("/admin");
            } else if (isRegister) {
                await register(form);
                nav("/dashboard");
            } else {
                await login(form.email, form.password);
                nav("/dashboard");
            }
            toast.success("Welcome!");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    const title = admin ? t.auth.adminTitle : (isRegister ? t.auth.registerTitle : t.auth.loginTitle);
    const desc = admin ? t.auth.adminDesc : (isRegister ? t.auth.registerDesc : t.auth.loginDesc);
    const testidPage = admin ? "page-admin-login" : (isRegister ? "page-register" : "page-login");

    return (
        <div data-testid={testidPage} className="container-x section-pad">
            <div className="max-w-md mx-auto">
                <div className="rounded-3xl border border-border bg-card p-8 sm:p-10 shadow-lg">
                    <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-white mb-6"
                        style={{ background: admin ? "linear-gradient(135deg, hsl(var(--gold-light)), hsl(var(--gold)))" : "linear-gradient(135deg, hsl(var(--royal)), hsl(var(--royal-deep)))" }}>
                        {admin ? <Shield size={24} /> : <LogIn size={24} />}
                    </div>
                    <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">{title}</h1>
                    <p className="mt-2 text-muted-foreground text-sm">{desc}</p>

                    <form onSubmit={submit} className="mt-6 space-y-4">
                        {isRegister && !admin && (
                            <>
                                <IconInput data-testid="auth-name" icon={User} placeholder={t.auth.namePh} value={form.name} onChange={(v) => setForm({ ...form, name: v })} required />
                                <IconInput data-testid="auth-phone" icon={Phone} placeholder={t.auth.phonePh} value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} required />
                            </>
                        )}
                        <IconInput data-testid="auth-email" icon={Mail} type="email" placeholder={t.auth.emailPh} value={form.email} onChange={(v) => setForm({ ...form, email: v })} required />
                        <IconInput data-testid="auth-password" icon={Lock} type="password" placeholder={t.auth.passwordPh} value={form.password} onChange={(v) => setForm({ ...form, password: v })} required />

                        <button data-testid="auth-submit" type="submit" disabled={loading} className="btn-royal w-full justify-center disabled:opacity-40">
                            {loading ? "…" : (isRegister ? t.auth.register : t.auth.login)}
                        </button>
                    </form>

                    {!admin && (
                        <div className="mt-6 text-center text-sm">
                            {isRegister
                                ? <Link data-testid="switch-to-login" to="/login" className="text-[hsl(var(--royal))] font-semibold hover:underline">{t.auth.switchToLogin}</Link>
                                : <Link data-testid="switch-to-register" to="/register" className="text-[hsl(var(--royal))] font-semibold hover:underline">{t.auth.switchToRegister}</Link>
                            }
                        </div>
                    )}
                </div>

                {!admin && (
                    <div className="mt-6 text-center">
                        <Link data-testid="link-admin-login" to="/admin/login" className="text-xs text-muted-foreground hover:text-[hsl(var(--royal))]">Admin login →</Link>
                    </div>
                )}
            </div>
        </div>
    );
};

const IconInput = ({ icon: Icon, ...rest }) => (
    <div className="relative">
        <Icon size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
        <input {...rest} onChange={(e) => rest.onChange(e.target.value)}
            className="w-full h-12 pl-11 pr-4 rounded-full border-2 border-border bg-background focus:border-[hsl(var(--royal))] focus:outline-none transition-colors" />
    </div>
);

export default AuthPage;
